import { CommonModule, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { IdentityModel } from '@core/models/identity.model';
import * as AuthActions from '@core/redux/auth/auth.actions';
import * as fromRoot from '@core/redux/index';
import { FileService } from '@core/services/file.service';
import { select, Store } from '@ngrx/store';
import { ImageCropperComponent, ImageCroppedEvent } from 'ngx-image-cropper';

@Component({
  selector: 'app-sign-up-step-1',
  templateUrl: './sign-up-step-1.component.html',
  styleUrls: ['./sign-up-step-1.component.less'],
  imports: [ReactiveFormsModule, NgClass, CommonModule, ImageCropperComponent],
  standalone: true,
  providers: [FileService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignUpStep1Component implements OnInit {
  private readonly store = inject<Store<fromRoot.State>>(Store);
  private readonly fileService = inject(FileService);
  private readonly formBuilder = inject(FormBuilder);
  private userIdentity: IdentityModel;
  private readonly destroyRef = inject(DestroyRef);
  form: FormGroup;
  isSending = false;
  validateErrors: any = {};
  avatarChangedEvent: any = '';
  croppedAvatar: any = '';
  showCrop = false;
  blobAvatar: Blob | null = null;

  constructor() {
    this.store.pipe(select(fromRoot.getAuthErrors), takeUntilDestroyed(this.destroyRef)).subscribe(errors => {
      this.validateErrors = errors;
    });

    this.store
      .pipe(select(fromRoot.getUser), takeUntilDestroyed(this.destroyRef))
      .subscribe((user: IdentityModel) => (this.userIdentity = user));

    this.store.pipe(select(fromRoot.getLoading), takeUntilDestroyed(this.destroyRef)).subscribe(isSending => {
      this.isSending = isSending;
    });
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      phone: [null, Validators.required],
      firstName: [null, Validators.compose([Validators.required, Validators.min(2)])],
      lastName: [null, Validators.min(2)],
      avatarId: [null],
    });

    this.form.setValue({
      phone: null,
      avatarId: null,
      firstName: this.userIdentity.firstName,
      lastName: this.userIdentity.lastName,
    });
  }

  onSubmit() {
    if (this.blobAvatar) {
      this.fileService.uploadAvatar(this.blobAvatar).subscribe(response => {
        this.form.controls['avatarId'].setValue(response['id']);
        this.store.dispatch(new AuthActions.SignUpStepPersonalDataAction(this.form.getRawValue()));
      });
    } else {
      this.store.dispatch(new AuthActions.SignUpStepPersonalDataAction(this.form.getRawValue()));
    }
  }

  onFileChangeEvent(event: any) {
    this.showCrop = true;
    this.avatarChangedEvent = event;
  }

  onImageCropped(event: ImageCroppedEvent) {
    this.croppedAvatar = event.objectUrl;
    this.blobAvatar = event.blob;
  }

  isFieldValid(field: string) {
    return !this.form.get(field).valid && this.form.get(field).touched;
  }

  displayFieldCss(field: string) {
    return {
      'input_theme-1_error': this.isFieldValid(field),
    };
  }
}
